<template>
  <nav aria-label="Page navigation">
    <ul class="s-paginator">
      <li class="s-paginator__item s-paginator__previous">
        <SButton
          action-type="quaternary"
          :disabled="currentPage === 1"
          prefix-icon="far fa-chevron-left"
          size="inline"
          :tag="props.pageTo ? props.tag : undefined"
          :to="props.pageTo ? props.pageTo(currentPage - 1) : undefined"
          @click="previousPage"
        >
          {{ tr("general.previous") }}
        </SButton>
      </li>
      <li v-if="currentPage > 2" class="s-paginator__item">
        <component
          :is="props.pageTo ? props.tag : 'button'"
          class="s-paginator__link"
          :to="props.pageTo ? props.pageTo(1) : undefined"
          :type="props.pageTo ? undefined : 'button'"
          @click="beginPage"
        >
          {{ 1 }}
        </component>
      </li>
      <li v-if="currentPage > 3" class="s-paginator__item">
        <button class="s-paginator__link" type="button">...</button>
      </li>
      <li v-if="currentPage !== 1" class="s-paginator__item">
        <component
          :is="props.pageTo ? props.tag : 'button'"
          class="s-paginator__link"
          :to="props.pageTo ? props.pageTo(currentPage - 1) : undefined"
          :type="props.pageTo ? undefined : 'button'"
          @click="previousPage"
        >
          {{ currentPage - 1 }}
        </component>
      </li>
      <li class="s-paginator__item s-paginator__item--active">
        <button class="s-paginator__link" type="button">
          {{ currentPage }}
        </button>
      </li>
      <li
        v-if="Number(currentPage) !== totalItemsToShow"
        class="s-paginator__item"
      >
        <component
          :is="props.pageTo ? props.tag : 'button'"
          class="s-paginator__link"
          :to="props.pageTo ? props.pageTo(Number(currentPage) + 1) : undefined"
          :type="props.pageTo ? undefined : 'button'"
          @click="nextPage"
        >
          {{ Number(currentPage) + 1 }}
        </component>
      </li>
      <li v-if="currentPage < totalItemsToShow - 2" class="s-paginator__item">
        <button class="s-paginator__link" type="button">...</button>
      </li>
      <li v-if="currentPage < totalItemsToShow - 1" class="s-paginator__item">
        <component
          :is="props.pageTo ? props.tag : 'button'"
          class="s-paginator__link"
          :to="props.pageTo ? props.pageTo(totalItemsToShow) : undefined"
          :type="props.pageTo ? undefined : 'button'"
          @click="endPage"
        >
          {{ totalItemsToShow }}
        </component>
      </li>
      <li class="s-paginator__item s-paginator__next">
        <SButton
          action-type="quaternary"
          :disabled="Number(currentPage) === totalItemsToShow"
          size="inline"
          suffix-icon="far fa-chevron-right"
          :tag="props.pageTo ? props.tag : undefined"
          :to="props.pageTo ? props.pageTo(Number(currentPage) + 1) : undefined"
          @click="nextPage"
        >
          {{ tr("general.next") }}
        </SButton>
      </li>
    </ul>
  </nav>
</template>

<script setup lang="ts">
/**
 * Pagination navigation with page numbers and prev/next controls.
 * Shows current page, adjacent pages, and ellipsis for skipped ranges.
 */
import type { Component } from "vue";

import { SButton } from "@solvari/common-fe";
import { useI18nGeneral } from "@solvari/translations";

const props = defineProps<{
  pageTo?: (page: number) => Record<string, unknown> | string;
  tag?: Component | "a" | "button" | "label" | "RouterLink" | "span";
  totalItemsToShow: number;
}>();

const { tr } = useI18nGeneral();

const currentPage = defineModel<number>("currentPage", { default: 1 });

function previousPage() {
  if (props.pageTo || currentPage.value <= 1) {
    return;
  }
  currentPage.value--;
}
function nextPage() {
  if (props.pageTo || currentPage.value >= props.totalItemsToShow) {
    return;
  }
  currentPage.value++;
}
function endPage() {
  if (props.pageTo || currentPage.value >= props.totalItemsToShow) {
    return;
  }
  currentPage.value = props.totalItemsToShow;
}
function beginPage() {
  if (props.pageTo) {
    return;
  }
  currentPage.value = 1;
}
</script>

<style lang="postcss" scoped>
@import "@/other/paginator/paginator.css";
</style>
